import { h, Component } from 'preact';
import ScrollBox from 'components/shared/scrollBox/scrollBox';
import { joinClasses, arrayUnion } from 'utils/utils';
import { getShowtimesData } from 'utils/bolt';
import { getQuickFilterOptions, getMovieName, getFiltersOrderReference } from 'utils/bolt/showtimes';
import { generateLanguageLabel } from 'utils/bolt/screeningAttributes';
import Store from 'store/store';
import s from 'components/bolt/showtimes/quickFilters.sss';
import { smoothScrollTo } from 'utils/smoothScroll';

// TODO: utils
const shallowEqual = (a, b) => a && b && arrayUnion(Object.keys(a), Object.keys(b)).every(k => a[k] === b[k]);

export default class QuickFilters extends Component {

	constructor(props) {
		super(props);
		this.state = {
			numBubbles: 0
		};
	}

	componentDidUpdate(prevProps, prevState) {
		const prevSelectedFilters = prevProps?.filters?.selected;
		if (this.filtersChanged(prevSelectedFilters, this.props.filters.selected)) {
			Store.emit('TRACK_FILTERS_CHANGE', this.props.filters.selected);
			Store.emit('SHOWTIMES_REFRESH');

			Store.on('FILTER_SCROLL_INTO_VIEW', (filterId) => {
				this.scrollBubbleIntoView(filterId);
			});
		}

		if (this.props.enablePeeking && (prevState.numBubbles !== this.state.numBubbles)) {
			if (this.$scrollBox?.$scroller.clientWidth < this.$bubbles.clientWidth) {
				this.$bubbles.classList.add(s.peekHorizontal);
			}
			if (this.$bubbles.clientHeight > this.$container.clientHeight) {
				this.$bubbles.classList.add(s.peekVertical);
			}
		}
	}

	filtersChanged(oldVersion, newVersion) {
		if (oldVersion === newVersion) {
			return false;
		}
		return Object.keys(newVersion).some(filter => {
			// If we went from no date to first date selected, it's an automated change
			if (filter === 'date' && !oldVersion.date && newVersion.date[0] === this.props.filters?.available?.date?.[0]) {
				return false;
			}
			return !shallowEqual(oldVersion[filter], newVersion[filter]);
		});
	}

	checkFiltersEmpty(selectedFilters) {
		return Object.keys(selectedFilters).every(filter => (
			filter === 'date' || !Array.isArray(selectedFilters[filter]) || !selectedFilters[filter].length
		));
	}

	updateFilters(name, value) {
		if (value === 'all') {
			Store.emit('FILTERS_RESET');
		} else {
			Store.emit('FILTER_TOGGLE', name, value);
		}
	}

	isActive(name, value) {
		const selectedFilters = this.props.filters.selected;
		let active;

		if (value === 'all') {
			active = this.checkFiltersEmpty(selectedFilters);
			return !!active;
		}

		active = selectedFilters?.[name]?.includes(value);
		if (name === 'combinedLanguage') {
			active = selectedFilters?.[name]?.find(a => shallowEqual(a, value));
		}
		return !!active;
	}

	sortMovies(ids) {
		const orderingReference = getFiltersOrderReference('movieId');
		return ids.slice().sort((a, b) => {
			if (orderingReference.length) {
				const orderA = orderingReference.indexOf(a);
				const orderB = orderingReference.indexOf(b);
				if (orderA > -1 || orderB > -1) {
					return orderB - orderA;
				}
			}
			return getMovieName(a).localeCompare(getMovieName(b));
		});
	}

	scrollBubbleIntoView(id) {
		const targetElement = document.getElementById(id);
		smoothScrollTo(this.$scrollBox.base, targetElement, 200, { direction: 'hor', relativeTo: 'view', offsetStart: 25, offsetEnd: 25 });
	}

	renderBubbles = (title) => {
		const possibleOptions = getQuickFilterOptions(this.props.possibleOptions);
		const allOption = {
			name: this.props?.appData?.copy?.baseCopy?.$ALL || 'All',
			value: 'all'
		};
		const allowedFilters = possibleOptions.reduce((acc, option) => {
			if (option.name === 'movie' && option.allowedValues?.[0]?.toLowerCase() === 'all') {
				const allMovieIds = this.props.filters?.available?.movie || [];
				option.allowedValues = this.sortMovies(allMovieIds);
			}

			if (option.name === 'combinedLanguage') {
				let combinedLanguageValues = option.allowedValues?.filter((val) => {
					let combinedLanguageAllowed = true;
					Object.keys(val).forEach((key) => {
						if (!this.props.filters.available[key]?.includes(val[key])) {
							combinedLanguageAllowed = false;
						}
					});
					return combinedLanguageAllowed;
				});

				combinedLanguageValues.forEach((val) => {
					acc.push({ name: 'combinedLanguage', value: val });
				});
			} else {
				let values = this.props.filters.available[option.name];
				if (!values) return acc;
				if (option.allowedValues) {
					values = option.allowedValues.filter(x => values.indexOf(x) !== -1);
				}
				for (let i = 0; i < values.length; i += 1) {
					acc.push({ name: option.name, value: values[i] });
				}
			}
			return acc;
		}, [allOption]);

		const showtimesAppData = getShowtimesData() || {};

		const bubbles = allowedFilters?.map(({ name, value }) => {
			let label = value;
			const baseCopy = this.props?.appData?.copy?.baseCopy || {};
			const filtersCopy = baseCopy.filters || {};
			const languagesCopy = baseCopy.languages || {};
			if (name === 'combinedLanguage') {
				label = generateLanguageLabel(value);
			} else if (name === 'originalLanguage') {
				label = baseCopy.$ORIGINAL_LANGUAGE;
			} else if (name === 'subtitles') {
				label = baseCopy.$LANGUAGE_SUB;
			} else if (name === 'dubbing') {
				label = baseCopy.$LANGUAGE_DUB;
			} else if (name === 'overVoice') {
				label = baseCopy.$LANGUAGE_OV;
			} else if (name === 'movie') {
				label = showtimesAppData.movieData?.[value]?.quickFilterBubble || getMovieName(value);
			} else if (value === 'all') {
				label = name;
			} else {
				label = filtersCopy[name] && filtersCopy[name][value];
			}

			label = label ? label.replace('<lang>', languagesCopy[value]) : value;

			return (
				<button
					key={`${name}-${value}`}
					id={value}
					onClick={() => {
						this.updateFilters(name, value);
						this.scrollBubbleIntoView(value);
					}}
					className={joinClasses(s.bubble, this.isActive(name, value) && s.active)}
				>
					{label}
				</button>
			);
		});

		if (title) {
			bubbles.unshift(<div className={s.title}>{title}</div>);
		}

		if (bubbles.length !== this.state.numBubbles) {
			this.setState({ numBubbles: bubbles.length });
		}

		return <div className={s.bubbles} ref={$ref => this.$bubbles = $ref}>{bubbles}</div>;
	}

	render() {
		const { isRTL, isSticky, title, wrappedView } = this.props;

		return (
			<div className={joinClasses(s.container, isSticky && s.sticky, wrappedView && s.wrappedView)} ref={e => this.$container = e}>
				<ScrollBox
					class={joinClasses(s.scrollBoxContainer)}
					device="mobile"
					tabIndex="0"
					onKeyDown={this.filtersKeyDown}
					ref={e => this.$scrollBox = e}
					hideScrollerBar
					isRTL={isRTL}
				>
					{this.renderBubbles(title)}
				</ScrollBox>
			</div>
		);
	}
}
